import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage, { AdminWindowSwitcher } from '../components/AdminShell';
import './PartnerAiCardsPage.css';

// Partner AI Cards (/partner/ai-cards): the AI trading cards made for a partner's own athletes,
// picked from a list and shown full size beside the photo each was made from, plus the athletes
// with no photo (so no card can be made) and a Request photo for each.
// Design handoff: "AI Card Menu Partner Site" (2026-11-05). Staff have their own page
// (AdminAiCardsPage). Backend: backend/app/routers/partner_ai_cards.py.

const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'name', label: 'Name' },
  { value: 'class', label: 'Class' },
];
// Same guard as the staff page: the confirming click must be its own deliberate click.
const CONFIRM_GUARD_MS = 400;
const TOAST_MS = 4000;
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Server timestamps are UTC without a tz suffix; parse as UTC, show local.
const parse = (iso) => (iso ? new Date(/([zZ])|([+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`) : null);
const fmtDate = (iso) => parse(iso)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) || '';
const fmtTime = (iso) => parse(iso)?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) || '';
const fmtShort = (iso) => parse(iso)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '';

const lastName = (name) => (name || '').trim().split(/\s+/).slice(-1)[0].toLowerCase();
const initials = (name) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

// "Class of 2029 · WR · Upper Canada College", skipping whatever is missing.
export const metaLine = (r, withSchool = true) => [
  r.class_year ? `Class of ${r.class_year}` : null, r.position, withSchool ? r.school : null,
].filter(Boolean).join(' · ');

export function sortCards(cards, sort) {
  const byNewest = (a, b) => (b.created_at || '').localeCompare(a.created_at || '') || b.card_id - a.card_id;
  const list = [...cards];
  if (sort === 'name') {
    return list.sort((a, b) => lastName(a.athlete_name).localeCompare(lastName(b.athlete_name))
      || (a.athlete_name || '').localeCompare(b.athlete_name || '') || byNewest(a, b));
  }
  if (sort === 'class') {
    // Grad year ascending, athletes with none last, then newest.
    return list.sort((a, b) => (a.class_year || 9999) - (b.class_year || 9999) || byNewest(a, b));
  }
  return list.sort(byNewest);
}

function NoPhotoRow({ athlete, onRequest }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const ask = async () => {
    setBusy(true);
    const ok = await onRequest(athlete.user_id);
    setBusy(false);
    if (ok) setDone(true);
  };
  return (
    <tr>
      <td className="pac-np__name">
        <Link className="adm-link" to={`/athletes/${athlete.user_id}`}>{athlete.name}</Link>
      </td>
      <td className="pac-np__meta">{metaLine(athlete, false)}</td>
      <td className="pac-np__asked">
        Last asked {athlete.last_photo_request_at ? fmtShort(athlete.last_photo_request_at) : 'never'}
      </td>
      <td className="pac-np__acts">
        <Link className="pac-textlink" to={`/athletes/${athlete.user_id}`}>View profile</Link>
        <button type="button" className="adm-btn adm-btn--sm pac-btn-soft" onClick={ask}
          disabled={busy || done} data-testid={`pac_request_photo_${athlete.user_id}`}>
          {busy ? 'Sending…' : done ? 'Requested' : 'Request photo'}
        </button>
      </td>
    </tr>
  );
}

function Preview({ card, onRemove, onShare, onDownload }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirmAt = useRef(0);
  const removeRef = useRef(null);
  useEffect(() => { setConfirming(false); setBusy(false); }, [card.card_id]);

  const onRemoveClick = async (e) => {
    if (!confirming) { confirmAt.current = Date.now(); setConfirming(true); return; }
    if (e.detail > 1 || Date.now() - confirmAt.current < CONFIRM_GUARD_MS) return;
    setBusy(true);
    const ok = await onRemove(card.card_id);
    if (!ok) { setBusy(false); setConfirming(false); }
  };
  const cancel = () => { setConfirming(false); if (removeRef.current) removeRef.current.focus(); };
  const who = card.athlete_name;

  return (
    <div className="adm__card pac-preview" data-testid="pac_preview">
      <div className="pac-preview__head">
        <div>
          <Link className="pac-preview__name" to={`/athletes/${card.user_id}`}>{who}</Link>
          <div className="pac-preview__meta">{metaLine(card)}</div>
        </div>
        <div className="pac-when">
          <div className="pac-when__date">Made {fmtDate(card.created_at)}</div>
          <div className="pac-when__time">{fmtTime(card.created_at)}</div>
        </div>
      </div>
      <div className="pac-preview__pair">
        <div>
          <div className="pac-label">AI card</div>
          {card.card_url
            ? <img className="pac-preview__img" src={card.card_url} alt={`AI card for ${who}`} />
            : <div className="pac-preview__img pac-frame">No image</div>}
        </div>
        <div>
          <div className="pac-label">Source photo</div>
          {card.source_photo_url
            ? <img className="pac-preview__img" src={card.source_photo_url} alt={`Original upload the card for ${who} was made from`} />
            : <div className="pac-preview__img pac-frame" aria-label="No source photo preview">{initials(who)}</div>}
        </div>
      </div>
      <div className="pac-actions">
        <a className="adm-btn adm-btn--primary" href={card.card_url || undefined} target="_blank" rel="noreferrer">
          View full size
        </a>
        <button type="button" className="adm-btn pac-btn-soft" onClick={() => onDownload(card)}>Download</button>
        <button type="button" className="adm-btn pac-btn-soft" onClick={() => onShare(card)}
          data-testid="pac_share">Share link</button>
        <span className="pac-actions__spacer" />
        <button ref={removeRef} type="button" className="adm-btn adm-btn--danger" onClick={onRemoveClick}
          disabled={busy} data-testid="pac_remove">
          {confirming ? 'Remove for good?' : 'Remove'}
        </button>
        {confirming && (
          <button type="button" className="adm-btn adm-btn--ghost" onClick={cancel} disabled={busy}>Cancel</button>
        )}
      </div>
    </div>
  );
}

export default function PartnerAiCardsPage() {
  const { API_URL } = useAuth();
  const [cards, setCards] = useState([]);
  const [noPhoto, setNoPhoto] = useState([]);
  const [sort, setSort] = useState('newest');
  const [selectedId, setSelectedId] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/partner/ai-cards`, cfg())
      .then((r) => {
        if (cancelled) return;
        setCards(r.data?.cards || []);
        setNoPhoto(r.data?.no_photo || []);
      })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'Failed to load AI cards.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API_URL]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  const sorted = useMemo(() => sortCards(cards, sort), [cards, sort]);
  // Default selection is the first row in the current sort; a stale id (removed card) falls back too.
  const selected = sorted.find((c) => c.card_id === selectedId) || sorted[0] || null;

  const removeCard = useCallback(async (cardId) => {
    const idx = sorted.findIndex((c) => c.card_id === cardId);
    const next = sorted[idx + 1] || sorted[idx - 1] || null;
    try {
      await axios.delete(`${API_URL}/partner/ai-cards/${cardId}`, cfg());
    } catch (e) {
      if (e.response?.status !== 404) {
        setToast({ error: true, text: e.response?.data?.detail || "Couldn't remove the card. Try again." });
        return false;
      }
    }
    setCards((prev) => prev.filter((c) => c.card_id !== cardId));
    setSelectedId(next ? next.card_id : null);
    setToast({ text: 'Card removed. It won’t be made again.' });
    return true;
  }, [API_URL, sorted]);

  const shareCard = useCallback(async (card) => {
    try {
      const r = await axios.post(`${API_URL}/partner/ai-cards/${card.card_id}/share`, null, cfg());
      const link = `${window.location.origin}${r.data.url}`;
      await navigator.clipboard.writeText(link);
      setToast({ text: 'Share link copied.' });
    } catch (e) {
      setToast({ error: true, text: e.response?.data?.detail || "Couldn't copy a share link." });
    }
  }, [API_URL]);

  const downloadCard = useCallback(async (card) => {
    if (!card.card_url) return;
    const filename = `${(card.athlete_name || 'card').replace(/[^A-Za-z0-9]+/g, '-')}-ai-card.png`;
    try {
      const blob = await (await fetch(card.card_url)).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // The image host refused a script read: open it so the browser can save it.
      window.open(card.card_url, '_blank', 'noopener');
    }
  }, []);

  const requestPhoto = useCallback(async (userId) => {
    try {
      const r = await axios.post(`${API_URL}/partner/ai-cards/athletes/${userId}/request-photo`, null, cfg());
      setNoPhoto((prev) => prev.map((a) => (a.user_id === userId
        ? { ...a, last_photo_request_at: r.data.last_photo_request_at } : a)));
      setToast({ text: 'Photo requested.' });
      return true;
    } catch (e) {
      setToast({ error: true, text: e.response?.data?.detail || "Couldn't send the photo request." });
      return false;
    }
  }, [API_URL]);

  return (
    <AdminPage
      active="partner-ai-cards"
      eyebrow="Clients"
      title="AI Cards"
      subtitle="Pick a card to see it full size beside the photo it was made from."
      actions={<AdminWindowSwitcher options={SORTS} value={sort} onChange={setSort} label="Sort cards" />}
    >
      {toast && (
        <div className={`settings-toast${toast.error ? ' settings-toast--error' : ''}`} role="status">{toast.text}</div>
      )}
      {error && <div className="adm-error">{error}</div>}

      {!loading && !error && noPhoto.length > 0 && (
        <div className="adm__card adm__card--flush pac-np" data-testid="pac_no_photo">
          <button type="button" className="pac-np__head" aria-expanded={open} onClick={() => setOpen(!open)}>
            <span className="pac-np__title">Athletes without a photo</span>
            <span className="adm-chip adm-chip--amber">{noPhoto.length}</span>
            <span className="pac-np__hint">A card can&apos;t be made until a photo is on the profile.</span>
            <span className={`pac-chevron${open ? ' pac-chevron--open' : ''}`} aria-hidden="true">▶</span>
          </button>
          {/* Collapsed with a class, not unmounted, so the rows stay in the page for assistive tech. */}
          <div className={`pac-np__body${open ? '' : ' pac-np__body--collapsed'}`}>
            <table className="adm-table pac-np__table">
              <tbody>
                {noPhoto.map((a) => <NoPhotoRow key={a.user_id} athlete={a} onRequest={requestPhoto} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading ? (
        <div className="adm__card"><div className="adm-loading">Loading AI cards…</div></div>
      ) : !error && (
        <div className="pac-grid">
          <div className="adm__card adm__card--flush pac-list">
            <div className="pac-list__head">
              <span className="pac-list__title">Cards</span>
              <span className="pac-list__count">{cards.length} total</span>
            </div>
            {sorted.length === 0 && <div className="adm-empty">No AI cards yet.</div>}
            {sorted.map((c) => (
              <button key={c.card_id} type="button"
                className={`pac-row${selected && selected.card_id === c.card_id ? ' pac-row--selected' : ''}`}
                aria-pressed={!!selected && selected.card_id === c.card_id}
                onClick={() => setSelectedId(c.card_id)} data-testid={`pac_row_${c.card_id}`}>
                {c.card_url ? <img className="pac-row__thumb" src={c.card_url} alt="" /> : <span className="pac-row__thumb" />}
                <span className="pac-row__text">
                  <span className="pac-row__name">{c.athlete_name}</span>
                  <span className="pac-row__meta">{metaLine(c)}</span>
                </span>
                <span className="pac-when">
                  <span className="pac-when__date">{fmtDate(c.created_at)}</span>
                  <span className="pac-when__time">{fmtTime(c.created_at)}</span>
                </span>
              </button>
            ))}
          </div>
          {selected && (
            <Preview card={selected} onRemove={removeCard} onShare={shareCard} onDownload={downloadCard} />
          )}
        </div>
      )}
    </AdminPage>
  );
}
